import { Component, DestroyRef, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Address3HelperService, AddressFormGroup } from '../address3-helper.service';
import { CustomValidators, regexValidators } from '@shared/customValidators';
import { ItalianProvince, ItalianTown } from '@shared/models/data-model';
import { auditTime, distinctUntilChanged, filter, first, map, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { ItalianTownsService } from '@shared/services/italian-towns.service';
import { NGXLogger } from 'ngx-logger';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';

@Component({
    selector: 'address3-italy',
    templateUrl: './address3-italy.component.html'
})
export class Address3ItalyComponent implements OnInit, OnDestroy {
    @Input()
    readonly = false;

    @Input()
    addressFormGroup: FormGroup<AddressFormGroup>;

    allProvinces: Array<ItalianProvince>;
    townsInProvince: Array<ItalianTown>;

    italianProvincesFiltered: Array<ItalianProvince> = [];
    italianProvincesTypeahead = new Subject<string>();

    italianTownsFiltered: Array<ItalianTown> = [];
    italianTownsTypeahead = new Subject<string>();

    constructor(
        private readonly logger: NGXLogger,
        private readonly destroyRef: DestroyRef,
        private readonly address3Helper: Address3HelperService,
        private readonly italianTownService: ItalianTownsService,
        private readonly dynatrace: DynatraceHelperService) {
    }

    get selectedCountry(): string {
        return this.addressFormGroup.controls.countryISO?.value;
    }

    get selectedProvince(): string {
        return this.addressFormGroup.controls.province?.value;
    }

    get provinceLabel(): string {
        return this.allProvinces?.find(p => p.code.toLowerCase() === this.selectedProvince?.toLowerCase())?.principal ?? '';
    }

    get showingSearchResult(): boolean {
        return Boolean(this.addressFormGroup.controls.searchRefCode?.value);
    }

    ngOnInit(): void {
        this.address3Helper.resetValidators(this.addressFormGroup);
        this.initValidators();
        this.address3Helper.updateValidity(this.addressFormGroup);

        if (!this.selectedProvince) {
            this.addressFormGroup.controls.city.disable({emitEvent: false});
        }

        const loadProvincesActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.ADDRESS_LOAD_ITA_PROVINCES);

        this.italianTownService.getProvinces().then(provinces => {
            this.allProvinces = provinces;

            this.dynatrace.leaveAction(loadProvincesActionId);

            this.italianProvincesFiltered = this.allProvinces;

            this.initProvinceTypeahead();
            this.initTownsTypeahead();

            this.addressFormGroup.controls.province.enable();

            this.addressFormGroup.controls.province.valueChanges.pipe(
                takeUntilDestroyed(this.destroyRef),
                distinctUntilChanged()
            ).subscribe(provinceCode => this.provinceChanged(provinceCode));

            if(this.selectedProvince) {
                this.provinceChanged(this.selectedProvince);
                if (this.showingSearchResult) {
                    this.invalidateSearchResultOnFirstEdit();
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.addressFormGroup.controls.city.enable();
    }

    private initProvinceTypeahead() {
        this.italianProvincesTypeahead.pipe(
            takeUntilDestroyed(this.destroyRef),
            auditTime(300),
            distinctUntilChanged(),
            tap(() => {
                this.italianProvincesFiltered = this.allProvinces;
            }),
            filter(term => term?.length > 0),
            map(term => term.toLowerCase())
        ).subscribe(term => {
            const codeMatch = this.allProvinces.filter(province => province.code.toLowerCase() === term);
            const startsWith = this.allProvinces.filter(province => province.principal.toLowerCase().startsWith(term));
            const contains = this.allProvinces.filter(province => province.principal.toLowerCase().includes(term));
            this.italianProvincesFiltered = [codeMatch, startsWith, contains].find(a => a.length > 0) ?? [];
        });
    }

    private initTownsTypeahead() {
        this.italianTownsTypeahead.pipe(
            takeUntilDestroyed(this.destroyRef),
            auditTime(300),
            distinctUntilChanged(),
            tap(() => {
                this.italianTownsFiltered = this.townsInProvince;
            }),
            filter(term => term?.length > 0),
            map(term => term.toLowerCase())
        ).subscribe(term => {
            const startsWith = this.townsInProvince.filter(town => town.town.toLowerCase().startsWith(term));
            const contains = this.townsInProvince.filter(town => town.town.toLowerCase().includes(term));
            this.italianTownsFiltered = [startsWith, contains].find(a => a.length > 0) ?? [];
        });
    }

    private provinceChanged(provinceCode: string) {
        this.townsInProvince = [];

        this.addressFormGroup.controls.city.disable({emitEvent: false});

        if (provinceCode) {
            const loadTownsActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.ADDRESS_LOAD_ITA_TOWNS);

            this.italianTownService.getTownsByProvince(provinceCode).then(towns => {
                this.townsInProvince = towns;
                this.italianTownsFiltered = towns;

                this.dynatrace.leaveAction(loadTownsActionId);

                if (!towns.some(t => t.town.toLowerCase() === this.addressFormGroup.controls.city.value?.toLowerCase())) {
                    this.addressFormGroup.controls.city.reset();
                }

                this.addressFormGroup.controls.city.enable();
            });
        }
    }

    private initValidators() {

        // line 1
        this.addressFormGroup.controls.line1.setValidators([
            Validators.required,
            Validators.maxLength(CustomValidators.getAddressMaxlength("line1"))
        ]);

        // line 2
        this.addressFormGroup.controls.line2.setValidators([
            Validators.maxLength(CustomValidators.getAddressMaxlength("line2"))
        ]);

        this.addressFormGroup.controls.city.setValidators([
            Validators.required,
            Validators.maxLength(CustomValidators.getAddressMaxlength("city"))
        ]);

        this.addressFormGroup.controls.province.setValidators([
            Validators.required,
            Validators.maxLength(CustomValidators.getAddressMaxlength("province"))
        ]);

        this.addressFormGroup.controls.postalCode.setValidators([
            Validators.required,
            Validators.pattern(regexValidators.postcodesByCountry.ITA),
            Validators.maxLength(CustomValidators.getAddressMaxlength("postalCode"))
        ]);
    }

    mapSearchResult(place: google.maps.places.PlaceResult) {
        const street = this.address3Helper.getGooglePlaceComponent(place, "route");
        const streetNumber = this.address3Helper.getGooglePlaceComponent(place, "street_number");
        const city = this.address3Helper.getGooglePlaceComponent(place, "administrative_area_level_3");
        const locality = this.address3Helper.getGooglePlaceComponent(place, "locality");
        const province = this.address3Helper.getGooglePlaceComponent(place, "administrative_area_level_2", true);
        const postalCode = this.address3Helper.getGooglePlaceComponent(place, "postal_code");

        this.logger.debug("Search Results ", {
            street, streetNumber, city, locality, province, postalCode, id: place.place_id
        });

        this.italianProvincesFiltered = this.allProvinces;
        this.addressFormGroup.patchValue({
            line1: [street, streetNumber].filter(Boolean).join(" "),
            line2: "",
            city: city || locality,
            province: province,
            postalCode,
            searchRefCode: place.place_id
        });

        this.invalidateSearchResultOnFirstEdit();
    }

    invalidateSearchResultOnFirstEdit() {
        this.addressFormGroup.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef),
            first()
        ).subscribe((values) => {
            this.logger.debug("Manually edited, invalidating search results", values);
            this.addressFormGroup.controls.searchRefCode.setValue("");
        });
    }

}
